import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { downloadPdf } from '../../lib/download';
import { Loader2, AlertTriangle, XCircle, Clock, BookOpen, Award, ClipboardCheck, MessageSquareText, Briefcase, FileDown, LogOut, Siren, Pencil, EyeOff } from 'lucide-react';
import { motifLabel } from '../candidat/AbandonPanel';
import { EcarterDialog } from '../candidat/EcarterDialog';
import { ProgressionTests } from '../suivi/ProgressionTests';
import { useAuth } from '../../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? format(parseISO(d.slice(0, 10)), 'dd/MM/yyyy') : '');

const VALEUR = {
  A: { label: 'Acquis', cls: 'bg-emerald-100 text-emerald-800' },
  ECA: { label: 'En cours', cls: 'bg-amber-100 text-amber-800' },
  NA: { label: 'Non acquis', cls: 'bg-red-100 text-red-800' },
};

export const FicheCandidatEntreprise = ({ open, candidatId, dossierId, viewAs, onClose, onEdit, apiBase = `${API_URL}/portail/entreprise` }) => {
  const { user } = useAuth();
  const canEcarter = ['admin', 'super_admin', 'responsable_pedagogique'].includes(user?.role);
  const [alerteAEcarter, setAlerteAEcarter] = useState(null);
  const ecarterAlerte = async (motif) => {
    try {
      const r = await axios.post(`${API_URL}/alertes-formateur/${alerteAEcarter.id}/ecarter`, { motif });
      setData(d => ({ ...d, alertes_formateur: d.alertes_formateur.map(a => a.id === r.data.id ? r.data : a) }));
      toast.success('Alerte écartée — motif enregistré');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); throw e; }
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !candidatId || !dossierId) return;
    setLoading(true); setData(null);
    axios.get(`${apiBase}/candidat/${candidatId}`, { params: { dossier_id: dossierId, ...(viewAs ? { view_as: viewAs } : {}) } })
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Erreur lors du chargement de la fiche'))
      .finally(() => setLoading(false));
  }, [open, candidatId, dossierId, viewAs, apiBase]);

  const a = data?.assiduite;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="fiche-candidat-entreprise">
        {loading || !data ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
        ) : (
          <div className="space-y-6">
            <SheetHeader>
              <div className="flex items-start justify-between gap-3 pr-6">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-600">Fiche candidat</p>
                  <SheetTitle className="text-2xl font-['Plus_Jakarta_Sans']" data-testid="fiche-candidat-nom">{data.candidat.nom} {data.candidat.prenom}</SheetTitle>
                  <p className="text-sm text-slate-500">{data.candidat.poste ? `${data.candidat.poste} · ` : ''}<span className="font-mono text-xs">{data.dossier.code}</span> {data.dossier.intitule}</p>
                  {data.candidat.entreprise_finale && <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Briefcase className="w-3 h-3" /> Entreprise : <span className="font-medium text-slate-700">{data.candidat.entreprise_finale}</span></p>}
                </div>
                <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={() => downloadPdf(`${apiBase}/candidat/${candidatId}/export-pdf`, `fiche_${data.candidat.nom}_${data.candidat.prenom}.pdf`, { dossier_id: dossierId, ...(viewAs ? { view_as: viewAs } : {}) })} data-testid="fiche-candidat-pdf-btn">
                  <FileDown className="w-4 h-4 mr-1.5" /> PDF
                </Button>
                {onEdit && (
                  <Button type="button" size="sm" className="shrink-0 bg-indigo-600 hover:bg-indigo-700" onClick={onEdit} data-testid="fiche-candidat-modifier-btn">
                    <Pencil className="w-4 h-4 mr-1.5" /> Modifier
                  </Button>
                )}
              </div>
            </SheetHeader>

            {data.abandon && (
              <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-900" data-testid="fiche-abandon">
                <p className="font-semibold flex items-center gap-2"><LogOut className="w-4 h-4" /> Abandon de la formation le {fmt(data.abandon.date)}</p>
                <p className="text-xs mt-1"><span className="font-medium">Motif :</span> {motifLabel(data.abandon.motif) || '—'}</p>
                {data.abandon.commentaire && <p className="text-xs mt-1 whitespace-pre-wrap"><span className="font-medium">Explication :</span> {data.abandon.commentaire}</p>}
              </div>
            )}

            {data.en_difficulte.actif && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="fiche-alerte-difficulte">
                <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Candidat en difficulté</p>
                <p className="text-xs mt-1 whitespace-pre-wrap">{data.en_difficulte.description}</p>
              </div>
            )}

            {data.alertes_formateur?.length > 0 && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900 space-y-2" data-testid="fiche-alertes-formateur">
                <p className="font-semibold flex items-center gap-2"><Siren className="w-4 h-4" /> Alertes du formateur ({data.alertes_formateur.length})</p>
                {data.alertes_formateur.map(a => (
                  <div key={a.id} className={`text-xs bg-white/70 rounded-md p-2 ${a.statut === 'ecartee' ? 'opacity-70' : ''}`} data-testid={`fiche-alerte-${a.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium">{fmt(a.date)} · {a.type_label}{a.signale_par_nom ? ` · ${a.signale_par_nom}` : ''}{a.statut === 'ecartee' && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[10px] font-semibold" data-testid={`fiche-alerte-ecartee-${a.id}`}>Écartée</span>}</p>
                      {canEcarter && a.statut !== 'ecartee' && <button type="button" onClick={() => setAlerteAEcarter(a)} className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-orange-300 text-orange-800 hover:bg-orange-100 text-[10px] font-semibold" data-testid={`fiche-alerte-ecarter-${a.id}`}><EyeOff className="w-3 h-3" /> Écarter</button>}
                    </div>
                    <p className="whitespace-pre-wrap mt-0.5">{a.description}</p>
                    {a.mesures && <p className="whitespace-pre-wrap mt-1 text-emerald-800"><span className="font-medium">Mesures correctives / conseils :</span> {a.mesures}</p>}
                    {a.statut === 'ecartee' && <p className="mt-1 text-slate-600 border-t border-orange-100 pt-1"><EyeOff className="w-3 h-3 inline mr-1" /><span className="font-medium">Écartée le {fmt((a.ecarte_le || '').slice(0, 10))} par {a.ecarte_par_nom}</span> : <span className="italic whitespace-pre-wrap">{a.motif_ecart}</span></p>}
                  </div>
                ))}
                {alerteAEcarter && (
                  <EcarterDialog open onOpenChange={(o) => !o && setAlerteAEcarter(null)} titre={`Écarter l'alerte « ${alerteAEcarter.type_label} »`} onConfirm={ecarterAlerte} testId="alerte-ecarter-dialog"
                    cause={<><p><span className="font-semibold">{alerteAEcarter.type_label}</span> — signalée le {fmt(alerteAEcarter.date)}{alerteAEcarter.signale_par_nom ? ` par ${alerteAEcarter.signale_par_nom}` : ''}</p><p className="whitespace-pre-wrap">{alerteAEcarter.description}</p>{alerteAEcarter.mesures && <p className="text-emerald-800">Mesures : {alerteAEcarter.mesures}</p>}</>} />
                )}
              </div>
            )}

            <Section icon={ClipboardCheck} title="Assiduité">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi label="Jours présents" value={`${a.jours_presents}/${a.jours_passes}`} />
                <Kpi label="Absences" value={a.nb_absences} sub={a.heures_absence > 0 ? `${a.heures_absence} h` : null} warn={a.nb_absences > 0} />
                <Kpi label="Retards" value={a.nb_retards} sub={a.minutes_retard > 0 ? `${a.minutes_retard} min` : null} warn={a.nb_retards > 0} />
                <Kpi label="Parcours" value={`${a.jours_passes}/${a.total_jours} j`} />
              </div>
              {(a.absences.length > 0 || a.retards.length > 0) && (
                <ul className="mt-3 space-y-1 text-xs text-slate-600">
                  {a.absences.map((x, i) => <li key={`a${i}`} className="flex gap-2"><XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5" /> Absence le {fmt(x.date)} ({x.heures} h){x.motif ? ` — ${x.motif}` : ''}{x.justifie ? ' · justifiée' : ''}</li>)}
                  {a.retards.map((x, i) => <li key={`r${i}`} className="flex gap-2"><Clock className="w-3.5 h-3.5 text-amber-500 mt-0.5" /> Retard le {fmt(x.date)} (arrivée {x.heure_arrivee}, {x.minutes} min){x.motif ? ` — ${x.motif}` : ''}</li>)}
                </ul>
              )}
            </Section>

            {!['candidat', 'client', 'financeur'].includes(user?.role) && (
              <Section icon={Award} title="Progression aux tests">
                <ProgressionTests dossierId={dossierId} candidatId={candidatId} compact />
              </Section>
            )}

            <Section icon={BookOpen} title="Journées de formation">
              <div className="flex flex-wrap gap-1.5">
                {data.journees.map(j => {
                  const cls = j.statut === 'present' ? 'bg-emerald-500 text-white' : j.statut === 'retard' ? 'bg-amber-400 text-white' : j.statut === 'absent' ? 'bg-red-500 text-white' : j.passee ? 'bg-slate-200 text-slate-600' : 'bg-white border border-dashed border-slate-300 text-slate-400';
                  return <span key={j.date} title={`${fmt(j.date)}${j.modules.length ? ' — ' + j.modules.join(', ') : ''}${j.statut ? ' · ' + j.statut : j.passee ? ' · non renseigné' : ' · à venir'}`} className={`text-[11px] px-2 py-1 rounded-md ${cls}`}>{format(parseISO(j.date), 'dd/MM')}</span>;
                })}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Vert : présent · Orange : retard · Rouge : absent · Gris : non renseigné · Pointillé : à venir</p>
            </Section>

            <Section icon={Award} title="Progression pédagogique">
              {(() => {
                const passees = [...data.journees].filter(j => j.passee).sort((x, y) => x.date.localeCompare(y.date));
                const PRES = { present: ['Présent', 'text-emerald-700 bg-emerald-50'], retard: ['Retard', 'text-amber-700 bg-amber-50'], absent: ['Absent', 'text-red-700 bg-red-50'], en_aft: ['En examen', 'text-cyan-700 bg-cyan-50'] };
                return (
                  <>
                    <p className="text-xs font-medium text-slate-500 mb-1">Parcours jour par jour ({passees.length})</p>
                    {passees.length === 0 ? <span className="text-xs text-slate-400">Aucune journée effectuée pour le moment</span> : (
                      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100 mb-3" data-testid="fiche-parcours-jours">
                        {passees.map(j => {
                          const p = PRES[j.statut] || ['Non renseigné', 'text-slate-500 bg-slate-100'];
                          return (
                            <li key={j.date} className="px-2 py-1.5 flex items-start gap-2 text-xs" data-testid={`fiche-parcours-${j.date}`}>
                              <span className="font-semibold text-slate-800 w-14 shrink-0">{format(parseISO(j.date), 'dd/MM/yy')}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${p[1]}`}>{p[0]}</span>
                              <span className="text-slate-600 min-w-0">{j.modules.length ? j.modules.join(', ') : '—'}{j.via_aft && <span className="ml-1 text-cyan-700">via {j.via_aft}</span>}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                );
              })()}
              <p className="text-xs font-medium text-slate-500 mb-1">Savoir-faire et connaissances évalués ({data.competences.length})</p>
              <div className="space-y-1">
                {data.competences.map(c => {
                  const v = VALEUR[c.valeur] || { label: c.valeur || '—', cls: 'bg-slate-100 text-slate-600' };
                  return <div key={c.competence_id} className="flex items-center justify-between gap-2 text-sm"><span className="text-slate-700">{c.titre}</span><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${v.cls}`}>{v.label}</span></div>;
                })}
                {data.competences.length === 0 && <span className="text-xs text-slate-400">Aucun savoir-faire évalué pour le moment</span>}
              </div>
            </Section>

            <Section icon={ClipboardCheck} title={`Évaluations (${data.evaluations.length})`}>
              {data.evaluations.length === 0 ? <span className="text-xs text-slate-400">Aucune évaluation réalisée</span> : (
                <ul className="divide-y divide-slate-100 text-sm">
                  {data.evaluations.map((e, i) => (
                    <li key={i} className="py-2 flex items-center justify-between gap-2" data-testid={`fiche-eval-${i}`}>
                      <div><p className="text-slate-800">{e.titre}{e.via_aft && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-cyan-100 text-cyan-800">via {e.via_aft}</span>}</p><p className="text-xs text-slate-400">{e.type} · {fmt(e.date)}</p></div>
                      <span className={`font-semibold ${e.pourcentage == null ? 'text-slate-400' : e.pourcentage >= 50 ? 'text-emerald-600' : 'text-red-600'}`}>{e.pourcentage == null ? (e.non_repondu ? 'Non répondu' : '—') : `${Math.round(e.pourcentage)} %`}{e.score != null && e.score_max ? <span className="text-xs text-slate-400 font-normal ml-1">({e.score}/{e.score_max})</span> : null}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {(data.periodes_entreprise.periodes.length > 0 || data.periodes_entreprise.entreprise) && (
              <Section icon={Briefcase} title="Périodes en entreprise">
                {data.periodes_entreprise.entreprise && <p className="text-sm text-slate-700">{data.periodes_entreprise.entreprise}{data.periodes_entreprise.poste ? ` — ${data.periodes_entreprise.poste}` : ''}</p>}
                <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
                  {data.periodes_entreprise.periodes.map((p, i) => <li key={i}>{p.label} : {fmt(p.date_debut)} → {fmt(p.date_fin)}</li>)}
                </ul>
              </Section>
            )}

            <Section icon={MessageSquareText} title={`Observations des formateurs (${data.observations.length})`}>
              {data.observations.length === 0 ? <span className="text-xs text-slate-400">Aucune observation</span> : (
                <ul className="space-y-2">
                  {data.observations.map((o, i) => (
                    <li key={i} className="rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid={`fiche-observation-${i}`}>
                      <p className="text-xs text-slate-400 mb-1">{fmt(o.date)} · {o.source}{o.auteur ? ` · ${o.auteur}` : ''}</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{o.texte}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <section>
    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2"><Icon className="w-4 h-4 text-cyan-600" /> {title}</h3>
    {children}
  </section>
);

const Kpi = ({ label, value, sub, warn }) => (
  <div className={`rounded-lg border p-3 ${warn ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`}>
    <p className={`text-xl font-bold font-['Plus_Jakarta_Sans'] ${warn ? 'text-amber-700' : 'text-slate-900'}`}>{value}{sub && <span className="text-xs font-normal text-slate-400 ml-1">{sub}</span>}</p>
    <p className="text-[11px] text-slate-500">{label}</p>
  </div>
);

